import Axios from "axios";
import { getConfig } from "@/config";
import store from "@/store";

let logoutTimer = null;

export default {
  install(app) {

    /**
     * Axios already configured in main.js:
     *
     * Axios.defaults.baseURL = /api
     * Axios.defaults.withCredentials = true
     *
     * withCredentials ensures cookies from oauth2-proxy
     * are sent with every request.
     */
    Axios.defaults.withCredentials = true;

    // ---------------- REQUEST INTERCEPTOR ----------------
    Axios.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => Promise.reject(error)
    );

    /**
     * ---------------------------------------------------------
     * RESPONSE INTERCEPTOR
     * ---------------------------------------------------------
     *
     * This interceptor handles authentication and permission errors.
     *
     * Possible responses:
     *
     * 401 → Session expired / user not authenticated
     * 403 → User authenticated but not authorized
     * 429 → Rate limiting from API gateway
     *
     * Important:
     * We ignore 401 from /api/auth/me during initial bootstrap
     * because the homepage /app3 must remain public.
     */

    Axios.interceptors.response.use(

      // Successful response
      (response) => response,

      // Error response
      async (error) => {

        const status = error?.response?.status;
        const requestUrl = String(error?.config?.url || '');

        const profileEndpoint = getConfig("AUTH.PROFILE_ENDPOINT") || "/api/auth/me";
        const isProfileBootstrapCall = requestUrl.includes(profileEndpoint);

        if (!status) return Promise.reject(error);

        /**
         * ---------------------------------------------------------
         * Special case: ignore auth/me errors during bootstrap
         * ---------------------------------------------------------
         *
         * When the app first loads:
         *
         * main.js calls:
         *   /oauth2/auth
         * then:
         *   /api/auth/me
         *
         * If user is not authenticated,
         * auth/me will return 401.
         *
         * We must NOT redirect to login automatically
         * because /app3 homepage is public.
         */
        if (isProfileBootstrapCall && (status === 401 || status === 403)) {
          return Promise.reject(error);
        }

        // stop processing if logout already triggered
        if (logoutTimer) return Promise.reject(error);

        /**
         * ---------------------------------------------------------
         * 401 Unauthorized
         * ---------------------------------------------------------
         *
         * Causes:
         * - oauth2 session expired
         * - user logged out
         * - cookie invalid
         *
         * Solution:
         * Logout and Redirect user to oauth2 login.
         */
        if (status === 401) {

          app.config.globalProperties.$alerts.toast.error(
            "لقد انتهت صلاحية الجلسة الخاصة بك ، أعد محاولة تسجيل الدخول"
          );

          logoutTimer = setTimeout(() => {
            store.dispatch("logout");
          }, 3000);

          return Promise.reject(error);
        }

        /**
         * ---------------------------------------------------------
         * 403 Forbidden
         * ---------------------------------------------------------
         *
         * User is authenticated but lacks permission.
         *
         * Example:
         * API endpoint requires role:
         *   [Authorize(Roles="Administrator")]
         *
         * but user role is different.
         */
        if (status === 403) {

          app.config.globalProperties.$alerts.toast.error(
            "ليس لديك صلاحيات لتنفيذ هذه العملية"
          );

          logoutTimer = setTimeout(() => {
            store.dispatch("logout");
          }, 3000);

          return Promise.reject(error);
        }

        /**
         * ---------------------------------------------------------
         * 429 Too Many Requests
         * ---------------------------------------------------------
         *
         * API gateway or backend rate limiting.
         */
        if (status === 429) {

          const retryAfter =
            parseInt(error.response?.headers["retry-after"]) || 3;

          app.config.globalProperties.$alerts.toast.error(
            `لقد تجاوزت الحد المسموح لعدد الطلبات. يرجى الانتظار ${retryAfter} ثواني ثم إعادة المحاولة.`
          );
        }

        return Promise.reject(error);
      }
    );

    // Make Axios available globally in Vue
    app.config.globalProperties.$http = Axios;
  },
};